import React from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import ReviewBody from "./ReviewBody";

/**
 * AnswerReviewDialog — unified review popup for Exercise / Exam / Trial Lab.
 *
 * Props:
 *   - open, onClose: standard Dialog controls
 *   - title: header title (e.g. "Review — Trial Test 1")
 *   - subtitle: optional secondary header line
 *   - headerChips: optional array of <Chip> elements rendered below the title
 *   - detailedGroups: NORMALIZED array (see AnswerReviewDialog/normalizers.js)
 *   - isLoading: when true, shows a spinner instead of the body
 *   - emptyMessage: text shown when detailedGroups is empty
 *
 * IMPORTANT: callers stay responsible for fetching their own data and for
 * passing the normalized `detailedGroups`. Adapters in `./normalizers.js`
 * convert each module's existing response shape into the unified format
 * without changing any API calls.
 */
const AnswerReviewDialog = ({
  open,
  onClose,
  title = "Review Answers",
  subtitle,
  headerChips,
  detailedGroups = [],
  isLoading = false,
  emptyMessage,
  useIndexNumbering = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          height: { xs: "100%", md: "90vh" },
          maxHeight: { xs: "100%", md: "90vh" },
          borderRadius: { xs: 0, md: 3 },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
          background: "linear-gradient(135deg, #0288D1 0%, #01579B 100%)",
          color: "white",
          py: { xs: 1.25, sm: 1.5 },
          px: { xs: 2, sm: 3 },
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{
              fontSize: { xs: "1rem", sm: "1.25rem" },
              lineHeight: 1.3,
              wordBreak: "break-word",
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              {subtitle}
            </Typography>
          )}
          {Array.isArray(headerChips) && headerChips.length > 0 && (
            <Box sx={{ display: "flex", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
              {headerChips.map((chip, i) =>
                React.isValidElement(chip) ? (
                  React.cloneElement(chip, {
                    key: chip.key ?? i,
                    size: chip.props.size ?? "small",
                    sx: {
                      bgcolor: "rgba(255,255,255,0.2)",
                      color: "white",
                      fontWeight: 600,
                      ...(chip.props.sx || {}),
                    },
                  })
                ) : (
                  <Chip
                    key={i}
                    size="small"
                    label={String(chip)}
                    sx={{
                      bgcolor: "rgba(255,255,255,0.2)",
                      color: "white",
                      fontWeight: 600,
                    }}
                  />
                )
              )}
            </Box>
          )}
        </Box>
        <IconButton onClick={onClose} sx={{ color: "white" }} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          height: "100%",
          display: "flex",
          overflow: "hidden",
        }}
      >
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="100%"
            height="100%"
          >
            <CircularProgress />
          </Box>
        ) : detailedGroups.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center", width: "100%" }}>
            <Typography color="text.secondary">
              {emptyMessage || "No review data available."}
            </Typography>
          </Box>
        ) : (
          <ReviewBody detailedGroups={detailedGroups} useIndexNumbering={useIndexNumbering} />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AnswerReviewDialog;
